import type { ChangesModel } from '@/entities/optimization'
import type {
	OptimizationGroupReviewModel,
	ProductsQueryRequestModel,
} from '@/shared/generated'

import React, { useEffect, useMemo } from 'react'
import { all, find, lt, pipe, size, values } from 'lodash/fp'
import {
	ChartNoAxesCombinedIcon,
	CheckIcon,
	DownloadIcon,
	PlayIcon,
	RefreshCwIcon,
	RotateCcwIcon,
	Undo2Icon,
	XIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	useOptimization,
	useUpdateProductsFieldsMutation,
} from '@/entities/optimization'
import { useOptimizationGroupQuery } from '@/entities/optimization-group'
import {
	useApproveReviewMutation,
	useOptimizationReviewsQuery,
} from '@/entities/optimization-review'
import { useSession } from '@/entities/session'

import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { OptimizationStatus } from '@/shared/generated'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { usePrevious } from '@/shared/hooks/usePrevious'
import { ampli } from '@/shared/lib/ampli'
import { useStore } from '@/shared/lib/datasheet/store'
import { useDrawer } from '@/shared/lib/drawer-manager'
import { useModals } from '@/shared/lib/modal-manager'
import { useProductsTableConfigStore } from '@/shared/lib/table-config'

export function OptimizationGroupActions({
	requestBody,
}: {
	requestBody?: Partial<
		Pick<ProductsQueryRequestModel, 'filters' | 'search' | 'order_by'>
	>
}) {
	/**
	 * Hooks
	 */
	const { t } = useTranslation()
	const { user } = useSession()
	const optimizationGroupId = useOptimizationGroupId()!
	const optimizationGroupQuery = useOptimizationGroupQuery(optimizationGroupId)

	const { showModal } = useModals()
	const localData = useStore((state) => state.localData)
	const setLocalData = useStore((state) => state.setLocalData)
	const drawer = useDrawer('OPTIMIZATION_SUMMARY')
	const updateProducts = useUpdateProductsFieldsMutation(optimizationGroupId)
	const {
		commit,
		start,
		stop,
		lock,
		unlock,
		create,
		createAndStart,
		optimization,
		isProcessAction,
		isOptimizationLoading,
	} = useOptimization(optimizationGroupId)
	const tableConfig = useProductsTableConfigStore()
	const { data: review } = useOptimizationReviewsQuery<
		OptimizationGroupReviewModel | undefined
	>({
		select: find<OptimizationGroupReviewModel>({
			optimization_group_id: optimizationGroupId,
		}),
	})
	useEffect(() => () => drawer.close(), [])
	/**
	 * Mutations
	 */
	const approveReview = useApproveReviewMutation()

	/**
	 * Computed
	 */

	const state = useMemo(
		() => getViewState(optimization?.status, isEditedData(localData)),
		[optimization?.status, localData]
	)
	const isSummaryAvailable = useMemo(
		() =>
			optimization?.status &&
			[OptimizationStatus.Locked, OptimizationStatus.Finished].includes(
				optimization.status
			),
		[optimization?.status]
	)
	const prevState = usePrevious(state)
	useEffect(() => {
		if (prevState === 'stop' && state === 'apply') {
			drawer.open({})
		}
	}, [state])

	/**
	 * Handlers
	 */
	const handleApproveReview = () =>
		approveReview.mutateAsync(optimizationGroupId)

	/**
	 * Buttons
	 */
	const approveBtn = (
		<Button
			disabled={isProcessAction || updateProducts.isPending}
			data-testid='optimization-action-approve'
			variant='primary'
			key='approve_prices'
			onClick={handleApproveReview}
		>
			{isProcessAction || updateProducts.isPending ? (
				<Spinner />
			) : (
				<CheckIcon />
			)}
			{t('approve_prices', {
				defaultValue: 'Approve prices',
			})}
		</Button>
	)
	const revertBtn = (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					data-testid='optimization-action-revert'
					key='revert'
					size='icon'
					onClick={() => setLocalData({})}
				>
					<Undo2Icon />
				</Button>
			</TooltipTrigger>
			<TooltipContent>
				{t('general_revert', {
					defaultValue: 'Revert',
				})}
			</TooltipContent>
		</Tooltip>
	)
	const saveBtn = (
		<Button
			disabled={
				isProcessAction || updateProducts.isPending || state === 'updating'
			}
			variant='warning'
			key='save'
			data-testid='optimization-action-save'
			onClick={async () => {
				ampli.tableSaveChanges()
				await updateProducts.mutateAsync(
					{
						changes: createProductDiffs(localData),
						ignore_empties: false,
					},
					{
						onError: (error) => {
							toast.error(error.message)
						},
					}
				)
				setLocalData({})
			}}
		>
			{isProcessAction || updateProducts.isPending || state === 'updating' ? (
				<Spinner />
			) : (
				<CheckIcon />
			)}
			{t('general_save_and_apply', {
				defaultValue: 'Save and apply',
			})}
		</Button>
	)

	const updatingBtn = <Skeleton className='inline-flex h-8 w-40 items-center' />
	const applyBtn = (
		<Button
			disabled={isProcessAction || updateProducts.isPending}
			key='apply'
			data-testid='optimization-action-apply'
			variant='primary'
			onClick={() => {
				ampli.repricingApplyPrices({
					'OG name': optimizationGroupQuery.data?.name,
					'Products amount': optimization?.total_products,
				})
				lock()
			}}
		>
			{isProcessAction || updateProducts.isPending ? (
				<Spinner />
			) : (
				<CheckIcon />
			)}
			{t('status_panel_apply_prices', {
				defaultValue: 'Apply prices',
			})}
		</Button>
	)
	const exportBtn = (
		<Button
			disabled={isProcessAction || updateProducts.isPending}
			key='export'
			data-testid='optimization-action-export'
			onClick={() => {
				ampli.repricingExportAppliedProices({
					'OG name': optimizationGroupQuery.data?.name,
					'Products amount': optimization?.total_products,
				})
				const context = {
					...requestBody,
					filters: [
						...(requestBody?.filters || []),
						{
							name: 'optimization_group_id',
							operation: Operators.IS as const,
							value: optimizationGroupId,
						},
					],
				}
				showModal('EXPORT_ASSORTMENT', {
					requireCallback: true,
					defaultName: `Applied prices ${optimizationGroupQuery.data?.name}`,
					tableConfig,
					context,
				})
			}}
		>
			<DownloadIcon />
			{t('status_panel_download_report', {
				defaultValue: 'Download report',
			})}
		</Button>
	)
	const stopBtn = (
		<Button
			disabled={isProcessAction || updateProducts.isPending}
			key='stop'
			data-testid='optimization-action-stop'
			variant='primary'
			onClick={stop}
		>
			{isProcessAction || updateProducts.isPending ? (
				<Spinner />
			) : (
				<RotateCcwIcon />
			)}
			{t('general_stop_and_cancel', {
				defaultValue: 'Stop and cancel',
			})}
		</Button>
	)
	const startBtn = (
		<Button
			disabled={isProcessAction || updateProducts.isPending}
			key='start'
			data-testid='optimization-action-start'
			variant='primary'
			onClick={start}
		>
			{isProcessAction || updateProducts.isPending ? <Spinner /> : <PlayIcon />}
			{t('products_start_optimization', {
				defaultValue: 'Start optimization',
			})}
		</Button>
	)
	const restartBtn = (
		<Button
			disabled={isProcessAction || updateProducts.isPending}
			key='restart'
			data-testid='optimization-action-restart'
			variant='primary'
			onClick={start}
		>
			{isProcessAction || updateProducts.isPending ? <Spinner /> : <PlayIcon />}
			{t('general_restart', {
				defaultValue: 'Restart',
			})}
		</Button>
	)
	const сreateAndStartBtn = (
		<Button
			disabled={isProcessAction || updateProducts.isPending}
			variant='primary'
			onClick={createAndStart}
		>
			{isProcessAction || updateProducts.isPending ? <Spinner /> : <PlayIcon />}
			{t('general_restart', {
				defaultValue: 'Restart',
			})}
		</Button>
	)
	const cancelBtn = (
		<Button
			disabled={isProcessAction || updateProducts.isPending}
			key='cancel'
			data-testid='optimization-action-cancel'
			onClick={unlock}
		>
			{isProcessAction || updateProducts.isPending ? (
				<Spinner />
			) : (
				<RotateCcwIcon />
			)}
			{t('general_discard', {
				defaultValue: 'Discard',
			})}
		</Button>
	)
	const revokeBtn = (
		<Button
			disabled={isProcessAction || updateProducts.isPending}
			key='revoke'
			data-testid='optimization-action-revoke'
			onClick={create}
		>
			{isProcessAction || updateProducts.isPending ? (
				<Spinner />
			) : (
				<RotateCcwIcon />
			)}
			{t('general_revoke', {
				defaultValue: 'Revoke',
			})}
		</Button>
	)
	const commitBtn = (
		<Button
			variant='warning'
			key='commit'
			data-testid='optimization-action-commit'
			onClick={commit}
		>
			{isProcessAction || updateProducts.isPending ? (
				<Spinner />
			) : (
				<RefreshCwIcon />
			)}
			{t('app.update_settings', {
				defaultValue: 'Update settings',
			})}
		</Button>
	)

	const getButtons = (state: string) => {
		switch (state) {
			case 'save':
				return [revertBtn, saveBtn]
			case 'commit':
				return [commitBtn]
			case 'updating':
				return [updatingBtn]
			case 'create_and_start':
				return [сreateAndStartBtn]
			case 'start':
				return [startBtn]
			case 'stop':
				return [stopBtn]
			case 'apply':
				if (review) {
					const reviewer = find({ id: user!.id }, review.reviewers)
					if (review.author_id === user!.id) {
						// when I`m author
						return [revokeBtn, applyBtn]
					} else if (reviewer) {
						// when I`m reviewer
						if (all({ is_approved: true }, review.reviewers)) {
							// approved by both reviewers
							return [revokeBtn, applyBtn]
						} else if (reviewer.is_approved) {
							return [revokeBtn]
						} else {
							return [revokeBtn, approveBtn]
						}
					} else {
						// when I`m random user
						return [revokeBtn, applyBtn]
					}
				}

				return [revokeBtn, applyBtn]
			case 'unlock':
				return [cancelBtn, exportBtn]
			case 'restart':
				return [revokeBtn, restartBtn]
			default:
				return ''
		}
	}
	if (isOptimizationLoading) {
		return <Skeleton className='inline-flex h-8 w-40' />
	}

	return (
		<div className='flex items-center space-x-2'>
			{getButtons(state!)}
			<OptimizationSummaryTrigger disabled={!isSummaryAvailable} />
		</div>
	)
}
const OptimizationSummaryTrigger = ({ disabled }: { disabled: boolean }) => {
	const { t } = useTranslation()
	const drawer = useDrawer('OPTIMIZATION_SUMMARY')

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					size='icon'
					variant='primary'
					key='view'
					disabled={disabled}
					data-testid='optimization-action-view'
					onClick={() => {
						if (drawer.isOpen) {
							drawer.close()
						} else {
							drawer.open({})
						}
					}}
				>
					{drawer.isOpen ? <XIcon /> : <ChartNoAxesCombinedIcon />}
				</Button>
			</TooltipTrigger>
			<TooltipContent side='bottom' align='end'>
				{t('opt.summary.disabled.tooltip', {
					defaultValue:
						'Optimization summary available only for "Finished" and "Applied" optimizations',
				})}
			</TooltipContent>
		</Tooltip>
	)
}
const getViewState = (status: OptimizationStatus, isEdit?: boolean) => {
	switch (true) {
		case (status === OptimizationStatus.New ||
			status === OptimizationStatus.Canceled) &&
			!isEdit:
			return 'start'
		case status === OptimizationStatus.Scheduled:
			return 'stop'
		case status === OptimizationStatus.Pending && !isEdit:
			return 'commit'
		case status == OptimizationStatus.Finished && !isEdit:
			return 'apply'
		case status === OptimizationStatus.Locked:
			return 'unlock'
		case status === OptimizationStatus.Failed && !isEdit:
			return 'restart'
		case isEdit &&
			![
				OptimizationStatus.Scheduled,
				OptimizationStatus.Locked,
				OptimizationStatus.Updating,
			].includes(status):
			return 'save'
		case status === OptimizationStatus.Updating:
			return 'updating'
		case status === OptimizationStatus.UpdatingFailed:
			return 'create_and_start'
		default:
			return null
	}
}

/**
 * Create product diffs from local changes to send to backend
 * @param localData - loal changes
 * @returns  - products list with local changes
 */
export const createProductDiffs = (
	localData: Record<string, object>
): ChangesModel[] => {
	const diffs: ChangesModel[] = []
	Object.entries(localData).forEach(([key, value]: [string, object]) => {
		const [, lineId, sku] = key.split('__')
		if (sku) {
			const diff = {
				...(localData[`${lineId}__`] || {}),
				...value,
				sku,
			}
			diffs.push(diff)
		}
	})

	return diffs
}
const isEditedData = pipe([values, size, lt(0)])
